// src/pages/admin/TicketScanner.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, RefreshCw, ScanLine, ShieldCheck } from "lucide-react";
import { auth } from "../firebase";

import "../pages/styles/ticketScanner.css";

const API_BASE = "https://jabuzwane.vercel.app/api";
const SCANNER_ELEMENT_ID = "ticket-scanner-viewport";

const TicketScanner = () => {
	const [searchParams] = useSearchParams();
	const scannerRef = useRef(null);
	const checkingRef = useRef(false);
	const resultRef = useRef(null);
	const linkedTicketRef = useRef(null);
	const [running, setRunning] = useState(false);
	const [result, setResult] = useState(null);
	const [checking, setChecking] = useState(false);
	const [cameraError, setCameraError] = useState(null);

	const handleDecoded = useCallback(async (text) => {
		if (checkingRef.current || resultRef.current) return;
		const ticketId = text.trim();
		if (!ticketId) return;

		const user = auth.currentUser;
		if (!user) {
			const sessionError = { valid: false, reason: "session_expired" };
			resultRef.current = sessionError;
			setResult(sessionError);
			return;
		}

		checkingRef.current = true;
		setChecking(true);
		try {
			const idToken = await user.getIdToken();
			const res = await fetch(`${API_BASE}/validate-ticket`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${idToken}`,
				},
				body: JSON.stringify({ ticketId }),
			});
			const data = await res.json();
			resultRef.current = data;
			setResult(data);
		} catch (e) {
			const networkError = { valid: false, reason: "network_error" };
			resultRef.current = networkError;
			setResult(networkError);
		} finally {
			checkingRef.current = false;
			setChecking(false);
		}
	}, []);

	// If the page was opened via the QR link itself (?ticketId=...), validate
	// immediately instead of waiting for the live camera to pick it up.
	useEffect(() => {
		const ticketIdFromLink = searchParams.get("ticketId");
		if (ticketIdFromLink && linkedTicketRef.current !== ticketIdFromLink) {
			linkedTicketRef.current = ticketIdFromLink;
			handleDecoded(ticketIdFromLink);
		}
	}, [handleDecoded, searchParams]);

	// Starts the live camera scanner. Guarded against React Strict Mode's
	// dev-only double-mount, which would otherwise call stop() before
	// start() has finished resolving.
	useEffect(() => {
		const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID);
		scannerRef.current = scanner;
		let cancelled = false;

		scanner
			.start(
				{ facingMode: "environment" },
				{ fps: 10, qrbox: { width: 260, height: 260 } },
				(decodedText) => handleDecoded(decodedText),
				() => {}
			)
			.then(() => {
				if (cancelled) {
					// Component was unmounted before start() finished — stop immediately.
					scanner.stop().then(() => scanner.clear()).catch(() => {});
					return;
				}
				setRunning(true);
			})
			.catch(() => {
				if (!cancelled) {
					setRunning(false);
					setCameraError("Camera access could not be started. Allow camera access, then try again.");
				}
			});

		return () => {
			cancelled = true;
			const currentScanner = scannerRef.current;
			if (!currentScanner) return;

			// Only call stop() if the scanner actually reached a running state —
			// otherwise stop() throws because there's nothing to stop yet.
			const state = currentScanner.getState ? currentScanner.getState() : null;
			const isRunning = state === 2; // Html5QrcodeScannerState.SCANNING

			if (isRunning) {
				currentScanner
					.stop()
					.then(() => currentScanner.clear())
					.catch(() => {});
			} else {
				try {
					currentScanner.clear();
				} catch (e) {
					// ignore — nothing was rendered yet
				}
			}
		};
	}, [handleDecoded]);

	const dismissResult = () => {
		resultRef.current = null;
		setResult(null);
	};

	const restartCamera = () => {
		setCameraError(null);
		window.location.reload();
	};

	return (
		<div className="scanner-page">
			<div className="scanner-shell">
				<header className="scanner-header">
					<div className="scanner-header__icon" aria-hidden="true"><ScanLine size={23} strokeWidth={2.3} /></div>
					<div>
						<p className="scanner-header__eyebrow">Jabuzwane events</p>
						<h1>Ticket check-in</h1>
						<p>Point the camera at a ticket QR code to check a guest in.</p>
					</div>
				</header>

				<div className="scanner-viewport-wrap">
					<div id={SCANNER_ELEMENT_ID} className="scanner-viewport" />
					<div className="scanner-corners" aria-hidden="true"><span /><span /><span /><span /></div>
				</div>

				<div className="scanner-status" role="status">
					{running ? <><Camera size={17} aria-hidden="true" /> Camera ready — scan the next ticket</> : <><RefreshCw size={17} className="scanner-status__spinner" aria-hidden="true" /> Requesting camera access…</>}
				</div>

				{cameraError && (
					<div className="scanner-camera-error" role="alert">
						<span>{cameraError}</span>
						<button type="button" onClick={restartCamera}><RefreshCw size={15} aria-hidden="true" /> Try again</button>
					</div>
				)}

				<div className="scanner-security-note"><ShieldCheck size={16} aria-hidden="true" /> Your signed-in admin session secures ticket validation.</div>
			</div>

			{checking && <div className="scanner-overlay scanner-overlay--checking">Checking…</div>}

			{result && !checking && (
				<div
					className={`scanner-overlay ${result.valid ? "scanner-overlay--valid" : "scanner-overlay--invalid"}`}
					onClick={dismissResult}
					role="button"
					tabIndex={0}
					onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") dismissResult(); }}
				>
					{result.valid ? (
						<>
							<div className="scanner-overlay__icon">✅</div>
							<div className="scanner-overlay__title">Valid ticket</div>
							<div className="scanner-overlay__meta">
								{result.ticket?.buyerName} · {result.ticket?.tierName} · #{result.ticket?.ticketNumber}
							</div>
						</>
					) : (
						<>
							<div className="scanner-overlay__icon">{result.reason === "already_used" ? "⚠️" : "❌"}</div>
							<div className="scanner-overlay__title">
								{result.reason === "already_used" && "Already used"}
								{result.reason === "not_found" && "Ticket not found"}
								{result.reason === "network_error" && "Connection error"}
								{result.reason === "session_expired" && "Admin session expired"}
							</div>
							{result.ticket && (
								<div className="scanner-overlay__meta">
									{result.ticket.buyerName} · {result.ticket.tierName}
								</div>
							)}
						</>
					)}
					<div className="scanner-overlay__hint">Tap anywhere to scan the next ticket</div>
				</div>
			)}
		</div>
	);
};

export default TicketScanner;
